import { deviceNavigationState } from '@/entities/device';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { auth } from '@/shared/lib';
import { useElementChange, useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';

/**
 * Backs the devices page with its tab, search and drawer state plus the facility context. Seeds state from the shared deviceNavigationState, reads supportFacilities and selectedFacility from the support and user stores, sets the document title and a default tab on mount, maintains the header's is-scrolling and has-login-banner classes, and returns the state with its tab, drawer and field handlers.
 *
 * @returns Devices page state, facility selections, shared navigation state, header ref and handlers.
 */
const useDevices = () => {
  /**
   * Holds the facilities the signed-in user supports. Read from the support store's supportFacilities field.
   *
   * @returns The support facility list.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * Holds the facility currently selected by the signed-in user. Read from the user store's selectedFacility field.
   *
   * @returns The selected facility.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);

  const headerRef = useRef<HTMLDivElement>(null);

  const isChangePosition = useElementChange(headerRef);

  const [state, setState] = useMergeState({
    activeTab: deviceNavigationState.tab || '1',
    clickedDevice: null,
    isVisibleDeviceInfoDrawer: false,
    searchByValue: deviceNavigationState.search.searchByValue || 'deviceId',
    searchValue: deviceNavigationState.search.searchValue || '',
    model: deviceNavigationState.select.model,
  });

  /**
   * Remembers the active devices tab so it survives leaving the page. Assigns tab onto the shared deviceNavigationState object.
   *
   * @param tab - Tab key to remember.
   * @returns Nothing.
   */
  const updateTabGoto = (tab: any) => {
    _.assign(deviceNavigationState, { tab });
  };

  /**
   * Remembers the active sub-tab of a devices tab so it survives leaving the page. Assigns subTab under the given key on the shared deviceNavigationState object.
   *
   * @param key - Devices tab the sub-tab belongs to.
   * @param subTab - Sub-tab value to remember.
   * @returns Nothing.
   */
  const updateSubTabGoto = (key: any, subTab: any) => {
    _.assign(deviceNavigationState, { [key]: subTab });
  };

  /**
   * Switches the devices page to another top-level tab. Does nothing when the tab is already active, otherwise records it on deviceNavigationState and stores it as activeTab.
   *
   * @param tab - Tab key to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      updateTabGoto(tab);
      setState({ activeTab: tab });
    }
  };

  /**
   * Opens or closes the device information drawer for a device row. Stores the clicked device, which is undefined when closing, and inverts isVisibleDeviceInfoDrawer.
   *
   * @param clickedDevice - Device whose details to show; omitted when closing.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedDevice?: any) => {
    setState({
      clickedDevice,
      isVisibleDeviceInfoDrawer: !state.isVisibleDeviceInfoDrawer,
    });
  };

  /**
   * Writes one search or filter field of the devices page state. Merges an object keyed by name holding value into the state.
   *
   * @param name - State key to write.
   * @param value - Value stored under that key.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Marks the devices page header as scrolled so it can render its elevated style. Adds the is-scrolling class to the headerRef element once window.scrollY leaves zero and removes it when the page returns to the top.
   *
   * @returns Nothing.
   */
  const handleScroll = () => {
    if (headerRef.current) {
      const { classList } = headerRef.current;
      if (window.scrollY === 0 && classList.contains('is-scrolling')) {
        headerRef.current.classList.remove('is-scrolling');
      } else if (window.scrollY !== 0 && !classList.contains('is-scrolling')) {
        headerRef.current.classList.add('is-scrolling');
      }
    }
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Devices`;
    if (!deviceNavigationState.tab) {
      updateTabGoto('1');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  return {
    state,
    supportFacilitiesRedux,
    selectedFacilityRedux,
    gotoRedux: deviceNavigationState,
    headerRef,
    updateSubTabGoto,
    navigateTab,
    toggleDisplayDrawer,
    onChange,
  };
};

export { useDevices };
